import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ElementRef,
  Injector,
  NgZone,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BmarkSpotRiskTableDefinitionFactory,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  SysFeatures,
  SystemConfigPromiseService,
  TraderGroupLookupService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OverlayPanel } from "@bfx/primeng/overlaypanel";
import { TranslateService } from "@ngx-translate/core";
import { Optional } from "ag-grid-community";
import { GridOptions } from "ag-grid-community/dist/lib/entities/gridOptions";
import { BmarkBaseRiskBlotterComponent } from "app/bfx-bmark-summary/blotters/spot-risk/bmark-base-risk-blotter.component";
import { PROFILE_SETTINGS_EXTCOLOUR_HIGHLIGHTING } from "app/bfx-user-settings/bfx-bmark-summary-settings-preferences.component";
import { Subject, Subscription } from "rxjs";
import { COLUMN_BMARK_TOTAL_EXTERNAL } from "shared/cubiq-services/model/BmarkSpotRiskTableDefinitionFactory";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import {
  COLUMN_ICONS,
  COLUMN_SELECTION,
} from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { provideBlotter } from "../../../bfx-base-blotter/bfx-base-blotter";
import { BfxBaseBlotterColumnSelectorService } from "../../../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterFilterService } from "../../../bfx-blotter-filter/bfx-blotter-filter.service";
import { BfxMouseModifierKeyService } from "../../../bfx-common/services/bfx-mouse-modifier-key.service";
import { BfxSaveProfileNotificationService } from "../../../bfx-common/services/bfx-save-profile-notification.service";
import { BfxUserProfileService } from "../../../bfx-common/services/bfx-user-profile.service";
import { GoldenLayoutService } from "../../../golden-layout/golden-layout-service";
import { BmarkSpotRiskContextMenuService } from "./services/bmark-spot-risk-context-menu.service";
import { BmarkSpotRiskDataService } from "./services/bmark-spot-risk-data.service";

const SPOT_RISK_PROFILE_KEY_SUFFIX: string = "bmarkSpotRisk";
const EXT_TG_COL: string = "tgStatusColumnHeaderComponent";

export type FormattedValueDateTotal = {
  intent: string;
  amount: string;
  valueDate: string;
};

export type ShowValueDateTotalsEvent = {
  valueDateTotals: FormattedValueDateTotal[];
  pairName: string;
  fixingDate: string;
  triggerEvent: { target: any };
};

/**
 * Spot risk blotter (normally wrapped in a bmark-summary-spot-risk).
 * Uses generic risk view-based functionality but has
 * specific spot-based services for data, table definition and context menu
 */
@Component({
  selector: "bmark-spot-risk-blotter",
  templateUrl: "./html/bmark-spot-risk-blotter.html",
  encapsulation: ViewEncapsulation.None,
  providers: [provideBlotter(BmarkSpotRiskBlotterComponent)],
  viewProviders: [BfxBaseBlotterColumnSelectorService],
  standalone: false,
})
export class BmarkSpotRiskBlotterComponent
  extends BmarkBaseRiskBlotterComponent
  implements AfterViewInit
{
  /** Single overlay component for showing value date totals for this blotter  */
  @ViewChild("valuedatetotals") overlay: OverlayPanel;

  /**
   * Are we aready waiting for overlay to hide before we reshow it?
   */
  private hideSubscription: Subscription = null;

  public valueDateModels: FormattedValueDateTotal[] = [];
  public valueDateTitle: string;

  /** Propagate model events on click from the instrument renderer when they click on Value Dates button */
  private valueDateSubject: Subject<ShowValueDateTotalsEvent>;

  /** When false we don't hide selection column */
  private manualRiskRoutingEnabled: boolean = true;
  private externalColoursEnabled: boolean;

  constructor(
    @Optional() _traderGroupLookup: TraderGroupLookupService,
    bfxBlotterElement: ElementRef,
    applicationRef: ApplicationRef,
    injector: Injector,
    public spotService: BmarkSpotRiskDataService,
    zone: NgZone,
    _tableDefinitionFactory: BmarkSpotRiskTableDefinitionFactory,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    blotterFilterService: BfxBlotterFilterService,
    columnSelectorService: BfxBaseBlotterColumnSelectorService,
    profileActionService: ProfilePromiseActionService,
    sysConfigService: SystemConfigPromiseService,
    mouseModifierKeyService: BfxMouseModifierKeyService,
    saveProfileNotificationService: BfxSaveProfileNotificationService,
    userProfileService: BfxUserProfileService,
    _contextMenuServices: BmarkSpotRiskContextMenuService,
  ) {
    super(
      _traderGroupLookup,
      bfxBlotterElement,
      applicationRef,
      injector,
      spotService,
      zone,
      _tableDefinitionFactory,
      layoutService,
      translationService,
      blotterFilterService,
      columnSelectorService,
      profileActionService,
      sysConfigService,
      mouseModifierKeyService,
      saveProfileNotificationService,
      userProfileService,
      _contextMenuServices,
    );

    // use BenchmarkSummary Preferences too
    this.profileActionService
      .getProfileConfig(ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences)
      .then((profileValue) => {
        this.mapUserSettings(profileValue);
        this.reassessColumnColours();
      });

    this.profileActionService
      .getProfileConfigUpdates(ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences)
      .subscribe((profileValue) => {
        this.mapUserSettings(profileValue);
        this.reassessColumnColours();
      });

    sysConfigService.getSystemConfig(SysFeatures.manualRiskRouting).then((value: string) => {
      this.manualRiskRoutingEnabled = value?.toUpperCase() == "Y";
    });
    this.valueDateSubject.subscribe((event) => {
      this.showValueDateTotals(event);
    });
  }

  private mapUserSettings(profileValue: any) {
    if (profileValue) {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != null && typeof savedSettings === "object") {
        if (
          savedSettings.hasOwnProperty(PROFILE_SETTINGS_EXTCOLOUR_HIGHLIGHTING) &&
          savedSettings.externalColoursModel !== null &&
          savedSettings.externalColoursModel == "y"
        ) {
          this.externalColoursEnabled = true;
        } else {
          this.externalColoursEnabled = false;
        }
      }
    } else {
      logger.out.warn(
        "BmarkSpotRiskBlotterComponent --> mapUserSettings no profile value " + profileValue,
      );
    }
  }

  private reassessColumnColours(): void {
    if (this.columnDefs) {
      this.columnDefs.forEach((colDef) => {
        if (colDef.cellClass == null) {
          colDef.cellClass = [];
        } else if (typeof colDef.cellClass === "string") {
          colDef;
          colDef.cellClass = colDef.cellClass.split(" ");
        } else if (typeof colDef.cellClass === "function") {
          return;
        }
        let columnClass: Array<string> = colDef.cellClass;
        if (Array.isArray(columnClass)) {
          if (this.externalColoursEnabled) {
            if (this.isExternalColumn(colDef)) {
              columnClass.unshift("externalColColour");
            }
          } else {
            const index = columnClass.indexOf("externalColColour");
            if (index != -1) {
              columnClass.splice(index, 1);
            }
          }
        }
      });
      this.gridOptions.api.redrawRows();
    }
  }

  protected override applyColumnDefinitions(): void {
    super.applyColumnDefinitions();
    this.reassessColumnColours();
  }

  private showValueDateTotals(event: ShowValueDateTotalsEvent) {
    this.valueDateTitle = `${event.pairName} - Fixing ${this.fixingName}`;
    this.valueDateModels = event.valueDateTotals;
    if (this.overlay) {
      const wasVisible = this.overlay.visible;
      if (wasVisible) {
        // Stupid overlay won't reposition itself without toggling visibility
        if (!this.hideSubscription) {
          this.hideSubscription = this.overlay.onHide.subscribe(() => {
            this.overlay?.show(event.triggerEvent, event.triggerEvent.target);
            this.hideSubscription.unsubscribe();
            this.hideSubscription = null;
          });
        }
      } else {
        this.overlay.show(event.triggerEvent);
      }
    }
  }

  protected override isExternalColumn(colDef: BfxColDef): boolean {
    if (colDef.headerComponent == EXT_TG_COL) {
      return true;
    }
    if (colDef.field == COLUMN_BMARK_TOTAL_EXTERNAL) {
      return true;
    }
    return false;
  }

  protected createsGridOptions(): GridOptions {
    let gridOptions = super.createsGridOptions();

    // As this is called from super constructor, need to initialise it here rather than statically
    this.valueDateSubject = new Subject();
    gridOptions.context.valueDateSubject = this.valueDateSubject;

    gridOptions.rowClassRules["pricing-error"] = (params) =>
      params.data?.pricingError?.orderCount > 0;
    return gridOptions;
  }

  public getBlotterType(): CubiqTS.BlotterType {
    return CubiqTS.BlotterType.ORDER;
  }

  /*     public getComponentConfiguration(): any {
          return super.getComponentConfiguration();
      } */

  public override getBlotterTypeStr(): string {
    return SPOT_RISK_PROFILE_KEY_SUFFIX;
  }

  protected onFullUpdateEvent(rows: Array<any>): void {
    // When manual risk routing enabled, we need to hide the  selection column when
    // moving to Recent Fixing, and reshow it when moving to Active
    if (
      this.manualRiskRoutingEnabled &&
      (rows == null || rows.length == 0) &&
      this.gridColumnApi != null &&
      this.columnDefs?.length > 0
    ) {
      const firstCol = this.columnDefs[0];
      if (firstCol?.field === COLUMN_SELECTION) {
        const isActive = this.service.getSelectedFixingData() != null;
        this.gridColumnApi.setColumnVisible(firstCol.colId, isActive);
        let iconCol = this.columnDefs.find((c) => c?.field === COLUMN_ICONS);
        if (iconCol) {
          this.gridColumnApi.setColumnVisible(iconCol.colId, !isActive);
        }
      }
    }
    super.onFullUpdateEvent(rows);
  }
}
